// Shared i18n configuration — safe to import from both client and server code.
// Locale dictionaries are NOT imported here: this module ships to the browser,
// and statically importing 22 JSONs would bundle every language for every
// merchant. The server keeps the full map in i18n.server.js; the client
// receives only the active language (window.__I18N__ from root.jsx) and
// lazy-loads others on demand (i18n.client.js).
export const SUPPORTED_LANGUAGES = [
  // `name` is a display-only label (NOT a translation key) — shown verbatim in
  // the Settings language picker. Format: "English — Native" so English-reading
  // merchants understand every option AND native speakers can spot their
  // language by script. The picker is wrapped in `notranslate`, so the native
  // scripts are not mangled by browser auto-translate.
  { code: "zh-CN", name: "Chinese (Simplified) — 简体中文" },
  { code: "zh-TW", name: "Chinese (Traditional) — 繁體中文" },
  { code: "cs", name: "Czech — Čeština" },
  { code: "da", name: "Danish — Dansk" },
  { code: "nl", name: "Dutch — Nederlands" },
  { code: "en", name: "English" },
  { code: "fi", name: "Finnish — Suomi" },
  { code: "fr", name: "French — Français" },
  { code: "de", name: "German — Deutsch" },
  { code: "it", name: "Italian — Italiano" },
  { code: "ja", name: "Japanese — 日本語" },
  { code: "ko", name: "Korean — 한국어" },
  { code: "no", name: "Norwegian — Norsk" },
  { code: "nb", name: "Norwegian Bokmål — Norsk bokmål" },
  { code: "nn", name: "Norwegian Nynorsk — Norsk nynorsk" },
  { code: "pl", name: "Polish — Polski" },
  { code: "pt-BR", name: "Portuguese (Brazil) — Português do Brasil" },
  { code: "pt-PT", name: "Portuguese (Portugal) — Português de Portugal" },
  { code: "so", name: "Somali — Soomaali" },
  { code: "es", name: "Spanish — Español" },
  { code: "sv", name: "Swedish — Svenska" },
  { code: "th", name: "Thai — ไทย" },
  { code: "tr", name: "Turkish — Türkçe" },
];

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

export const DEFAULT_LANGUAGE = "en";

/**
 * Map any incoming locale tag (Shopify admin locale, saved preference, …)
 * onto one of our supported language codes, falling back to English.
 *
 * Handles BCP 47 tags ("pt-BR", "fr-CA"), underscore variants ("pt_BR"),
 * wrong case ("PT-br") and bare language codes ("zh" -> "zh-CN").
 */
export function matchSupportedLanguage(locale) {
  if (!locale || typeof locale !== "string") return DEFAULT_LANGUAGE;

  const clean = locale.trim().replace(/_/g, "-");
  if (SUPPORTED_LANGUAGE_CODES.includes(clean)) return clean;

  const lower = clean.toLowerCase();
  const caseInsensitive = SUPPORTED_LANGUAGE_CODES.find(
    (code) => code.toLowerCase() === lower,
  );
  if (caseInsensitive) return caseInsensitive;

  const base = lower.split("-")[0];
  const baseMatch = SUPPORTED_LANGUAGE_CODES.find(
    (code) =>
      code.toLowerCase() === base || code.toLowerCase().startsWith(`${base}-`),
  );
  return baseMatch || DEFAULT_LANGUAGE;
}
